import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  InputGroup,
  MenuItem,
  Spinner,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Flex, Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { FiEye, FiTrash } from 'react-icons/all';
import download from 'downloadjs';
import {
  Datepicker,
  Heading,
  Input,
  Select,
  Table,
  Textarea,
} from '~/components';
import { GeneralInformation } from '~/pages/Portal/Sinisters/GeneralInformation';
import {
  useDebitNoteConfiguration,
  useSinister,
  useSinistersIssuerAsSelect,
} from '~/hooks/useSinisters/useSinisters';
import apiSinister from '~/utils/apiSinister';
import { queryClient } from '~/utils/react-query';
import { SinistersEditTextForm } from '~/pages/Portal/Sinisters/SinistersDebitNote/SinistersEditTextForm';
import { SinistersNewIssuerForm } from '~/pages/Portal/Sinisters/SinistersDebitNote/SinistersNewIssuerForm';
import { getDebitNote } from '~/services/Sinisters';
import { SinisterDebitNote } from '~/types/sinisters';
import { formatCurrency } from '~/utils/formatCurrency';

export const SinistersDebitNoteForm: React.FC<{
  sinisterId: number;
  link?: boolean;
}> = ({ sinisterId, link = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isFetching, setFetching] = useState(false);
  ('');
  const [referente, setReferente] = useState<string>('');
  const [data_vencimento, setDataVencimento] = useState<Date | undefined>(
    undefined,
  );
  const [emitente, setEmitente] = useState<number | undefined>(undefined);
  const [valor, setValor] = useState<number>(0);
  const [valorFormatado, setValorFormatado] = useState<string>('R$ 0,00');
  const [nota_debito, setNotaDebito] = useState<
    | {
        documento: string;
        data: string;
        empresa: string;
        valor: string;
      }
    | undefined
  >(undefined);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (link) handleOpen();
  }, []);

  const parse = (valueAsString: string) => {
    let valueAsNumber = 0;
    try {
      let parsedValueAsString = valueAsString.replace(/\D/g, '');
      parsedValueAsString = parsedValueAsString.padStart(2 + 1, '0');
      parsedValueAsString = `${parsedValueAsString.substring(
        0,
        parsedValueAsString.length - 2,
      )}.${parsedValueAsString.slice(2 * -1)}`;
      valueAsNumber = parseFloat(parsedValueAsString);
    } catch {
      valueAsNumber = 0;
    }
    return valueAsNumber;
  };

  const { data: issuer, isLoading: isIssuerLoading } =
    useSinistersIssuerAsSelect();
  const { data, isLoading, refetch } = useSinister(sinisterId);
  const { data: configuration } = useDebitNoteConfiguration();

  const handleOpen = () => {
    if (configuration) {
      setReferente(configuration.nota_de_debito_texto_padrao);
      const date = new Date(Date.parse(configuration.data_vencimento));
      setDataVencimento(date);
    }
    onOpen();
  };

  useEffect(() => {
    if (configuration) {
      setReferente(configuration.nota_de_debito_texto_padrao);
    }
  }, [configuration]);

  const handleClose = () => {
    clearValues();
    onClose();
  };
  const clearValues = () => {
    setNotaDebito(undefined);
    setDataVencimento(undefined);
    setValor(0);
    setValorFormatado('R$ 0,00');
    setEmitente(undefined);
    clearErrors();
  };

  async function handleNotaDebito() {
    clearErrors();
    !data_vencimento &&
      setError('data_vencimento', {
        type: 'required',
        message: 'Data de vencimento é obrigatória',
      });

    !emitente &&
      setError('emitente', {
        type: 'required',
        message: 'Emitente é obrigatório',
      });

    !valor &&
      setError('valor', {
        type: 'required',
        message: 'Valor é obrigatório',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    if (!emitente && !data_vencimento) {
      return;
    }

    const form = {
      referente,
      data_de_vencimento: data_vencimento?.toISOString().substring(0, 10),
      id_emitente: emitente,
      valor,
      numero_nota_debito: nota_debito?.documento,
    };

    const request = apiSinister.post<{ numero_nota_debito: string }>(
      `/enviar-nota-debito/gerar-numero-nota-debito/`,
      form,
    );

    request
      .then(res => {
        toast({
          title: `Número da nota de débito gerado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        const empresa = issuer?.filter(
          it => it.value === emitente!.toString(),
        )[0];
        setNotaDebito({
          documento: res.data.numero_nota_debito,
          data: data_vencimento!.toLocaleDateString('pt-br'),
          empresa: empresa!.label,
          valor: valorFormatado,
        });
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 413) {
          toast({
            title: `Tamanho de requisição excedida`,
            description: 'Limite máximo para tamanho de arquivos é de 5MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          // erros de valid
          const errorsValidate = Object.entries(error.response.data);

          errorsValidate.forEach(err => {
            toast({
              title: `Erros ao gerar número da nota de débito`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao gerar número da nota de débito`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  async function handleCreate() {
    clearErrors();
    !data_vencimento &&
      setError('data_vencimento', {
        type: 'required',
        message: 'Data de vencimento é obrigatória',
      });

    !emitente &&
      setError('emitente', {
        type: 'required',
        message: 'Emitente é obrigatório',
      });

    valor === 0 &&
      setError('valor', {
        type: 'required',
        message: 'Valor é obrigatório',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    if (!emitente && !data_vencimento) {
      return;
    }
    const form = {
      referente,
      data_de_vencimento: data_vencimento?.toISOString().substring(0, 10),
      id_emitente: emitente,
      valor,
      numero_nota_debito: nota_debito?.documento,
    };

    setFetching(true);

    const request = apiSinister.put(`enviar-nota-debito/${sinisterId}/`, form);

    request
      .then(() => {
        toast({
          title: `Nota de débito enviada`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        clearValues();
        onClose();
        queryClient.invalidateQueries('sinistersList');
        queryClient.invalidateQueries(['sinisterLog', sinisterId]);
        queryClient.invalidateQueries(['sinister', sinisterId]);
        queryClient.invalidateQueries('sinisterDashboard');
        queryClient.invalidateQueries(['sinisterDocuments', sinisterId]);
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 413) {
          toast({
            title: `Tamanho de requisição excedida`,
            description: 'Limite máximo para tamanho de arquivos é de 5MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          // erros de valid

          const errorsValidate = Object.entries(error.response.data);

          errorsValidate.forEach(err => {
            if (err[0] === 'numero_nota_debito') {
              toast({
                title: `Erros ao enviar nota de débito`,
                description: 'Gere uma nota de débito antes de enviar.',
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
              });
            } else {
              toast({
                title: `Erros ao enviar nota de débito`,
                description: String(err[1]),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
              });
            }
          });
        } else {
          toast({
            title: `Erro ao enviar nota de débito`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  function handleDownload() {
    setIsDownloading(true);
    const form: SinisterDebitNote = {
      referente,
      data_de_vencimento: data_vencimento!.toISOString().substring(0, 10),
      id_emitente: emitente!,
      valor,
      numero_nota_debito: nota_debito!.documento!,
    };
    getDebitNote(sinisterId, form)
      .then(r => {
        if (r?.data?.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(err => {
        toast({
          title: 'Erro ao gerar nota de débito',
          description: 'Entre em contato para mais informações',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  }

  function handleDataVencimento(e: any) {
    setDataVencimento(e);
    clearErrors('data_vencimento');
  }

  function handleEmitente(e: any) {
    setEmitente(e.target.value);
    if (e.target.options.selectedIndex !== 0) {
      clearErrors('emitente');
    }
  }

  function handleReferenteChage(e: any) {
    setReferente(e.target.value);
    clearErrors('referente');
  }

  function handleValor(e: any) {
    const value = parse(e.target.value);
    setValor(value);
    setValorFormatado(formatCurrency(value));
    clearErrors('valor');
  }
  function handleDelete() {
    setNotaDebito(undefined);
  }

  const isDisable =
    !!(!data_vencimento || !emitente || valor === 0) || !!nota_debito;

  return (
    <>
      <MenuItem onClick={handleOpen}>Enviar nota de débito</MenuItem>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="lg"
        closeOnOverlayClick={!isFetching}
      >
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton isDisabled={isFetching} />
          <DrawerHeader>Enviar nota de débito</DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" maxW={600} w="100%">
              <Stack gridGap={4}>
                <GeneralInformation sinister={data!} isAdmin />
                <Heading>Nota de débito </Heading>
                <FormControl isInvalid={errors.referente}>
                  <FormLabel
                    htmlFor="writeUpFile"
                    style={{ fontSize: '14px' }}
                    justifyContent="space-between"
                    display="flex"
                  >
                    Referente
                  </FormLabel>
                  <Textarea
                    name="referente"
                    resize="none"
                    error={errors?.referente}
                    value={referente}
                    fontSize="15px"
                    placeholder="Referente"
                    onChange={e => handleReferenteChage(e)}
                  />
                  <SinistersEditTextForm sinisterId={sinisterId} />
                </FormControl>
                <InputGroup>
                  <FormControl
                    isInvalid={errors.data_vencimento}
                    marginRight="10px"
                  >
                    <FormLabel
                      htmlFor="writeUpFile"
                      style={{ fontSize: '14px' }}
                      justifyContent="space-between"
                      display="flex"
                    >
                      Data de vencimento *
                    </FormLabel>
                    <Datepicker
                      onChange={e => handleDataVencimento(e)}
                      selectedDate={data_vencimento}
                      error={errors.data_vencimento}
                      isClearable={!nota_debito}
                      isDisabled={isFetching || !!nota_debito}
                    />
                  </FormControl>
                  <FormControl isInvalid={errors.emitente}>
                    <FormLabel
                      htmlFor="writeUpFile"
                      style={{ fontSize: '14px' }}
                    >
                      Emitente *
                    </FormLabel>
                    {/* eminente */}
                    <InputGroup>
                      <Select
                        name="emitente"
                        placeholder="Selecione"
                        error={errors.emitente}
                        onChange={e => handleEmitente(e)}
                        fontSize="15px"
                        value={emitente}
                        isDisabled={isFetching || !!nota_debito}
                      >
                        {!isIssuerLoading &&
                          issuer?.map(t => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                      </Select>
                      <SinistersNewIssuerForm />
                    </InputGroup>
                  </FormControl>
                </InputGroup>
                <InputGroup alignItems="baseline">
                  <FormControl isInvalid={errors.valor}>
                    <FormLabel
                      htmlFor="writeUpFile"
                      style={{ fontSize: '14px' }}
                    >
                      Valor *
                    </FormLabel>
                    <Flex alignItems="start">
                      <Input
                        name="valor"
                        error={errors.valor}
                        onChange={e => handleValor(e)}
                        value={valorFormatado}
                        isDisabled={isFetching || !!nota_debito}
                      />
                      <Button
                        type="submit"
                        color="white"
                        background="gray.900"
                        h="40px"
                        marginLeft="10px"
                        _hover={{
                          bgColor: 'black',
                        }}
                        size="sm"
                        width="30%"
                        onClick={handleSubmit(handleNotaDebito)}
                        isDisabled={isDisable}
                      >
                        Gerar nota de débito
                      </Button>
                    </Flex>
                  </FormControl>
                </InputGroup>
                <Table type="secondary" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Documento</Th>
                      <Th>Data</Th>
                      <Th
                        whiteSpace="nowrap"
                        style={{ textTransform: 'inherit' }}
                      >
                        Empresa a faturar
                      </Th>
                      <Th whiteSpace="nowrap">Valor (R$)</Th>
                      <Th textAlign="center">Ações</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {nota_debito && (
                      <Tr key={`${1}`}>
                        <Td>{nota_debito.documento}</Td>
                        <Td>{nota_debito.data}</Td>
                        <Td>{nota_debito.empresa}</Td>
                        <Td>{nota_debito.valor}</Td>
                        <Td
                          style={{
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                            minWidth: '120px',
                          }}
                        >
                          <Tooltip label="Download" placement="top">
                            <IconButton
                              w="25px"
                              fontSize="20px"
                              background="transparent"
                              aria-label="Remove type"
                              icon={<Icon as={FiEye} />}
                              onClick={() => handleDownload()}
                              isDisabled={isDownloading}
                            />
                          </Tooltip>
                          <Tooltip label="Deletar" placement="top">
                            <IconButton
                              w="25px"
                              fontSize="20px"
                              background="transparent"
                              aria-label="Remove type"
                              icon={<Icon as={FiTrash} />}
                              onClick={() => handleDelete()}
                              isDisabled={isDownloading}
                            />
                          </Tooltip>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Stack>
            </Stack>
            <Button
              type="submit"
              onClick={handleSubmit(handleCreate)}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px' }}
              isDisabled={isFetching}
            >
              Confirmar{' '}
              {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
            </Button>
            <Button
              type="submit"
              onClick={handleClose}
              color="black"
              background="transparent"
              style={{
                marginTop: '20px',
                marginLeft: '10px',
              }}
              isDisabled={isFetching}
            >
              Cancelar
            </Button>
            <Text
              style={{ fontSize: '12px', color: 'gray.500', marginTop: '20px' }}
            >
              * Campos obrigatórios
            </Text>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
